/**
 * Google Analytics 4 (GA4) Integration
 *
 * プライバシー配慮事項：
 * - 個人情報（LINE userId、氏名等）は送信しない
 * - ハッシュ化されたユーザーIDのみ使用
 * - patient_id は内部ID（個人情報ではない）として送信
 * - 開発環境では無効化
 * - 同意前: ページビュー・イベントは送信、user_id/patient_idは付与しない
 * - 同意後: フルトラッキング（user_id, patient_id付与）
 */

import ReactGA from 'react-ga4';
import { sanitizeAbsoluteUrl } from './analyticsUrl';

const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true';

interface AnalyticsConfig {
  enabled: boolean;
  initialized: boolean;
}

const config: AnalyticsConfig = {
  enabled: false,
  initialized: false,
};

/**
 * 現在選択中の患者ID（イベント送信時に自動付与）
 */
let currentPatientId: number | null = null;

/**
 * ユーザー同意状態（同意後はフルトラッキング有効）
 */
let hasUserConsent: boolean = false;

/**
 * SHA-256ハッシュ生成（ブラウザネイティブ）
 */
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * 計測に送る URL を洗って gtag に設定する。
 *
 * `page_location` / `page_referrer` は GA4 の自動収集パラメータで、**ページビューに限らず
 * すべてのイベントに付く**。送信のたびに引数で渡す形だと、イベントを 1 つ足すたびに
 * 書き忘れる余地が残るため、`gtag('set')` で以降のイベント全体に効かせる。
 *
 * 呼ぶのは 2 箇所。初期化直後（最初の page_view より先に `login` が飛びうるため）と、
 * ページビュー送信の直前（SPA のルート変更に追従するため）。
 */
function applySanitizedUrl(): void {
  ReactGA.set({
    page_location: sanitizeAbsoluteUrl(window.location.href),
    page_referrer: sanitizeAbsoluteUrl(document.referrer),
  });
}

/**
 * GA4基本初期化（アプリ起動時に呼び出し）
 * - 同意に関係なくページビュー・イベントをトラッキング
 * - user_id, patient_id は同意後のみ送信（enableFullTracking() 呼び出し後）
 */
export function initializeAnalytics(): void {
  // 既に初期化済みの場合はスキップ
  if (config.initialized) {
    console.log('[Analytics] Already initialized');
    return;
  }

  // 開発環境では無効化
  if (DEBUG_MODE) {
    console.log('[Analytics] DEBUG_MODE: GA4 disabled');
    return;
  }

  // Measurement IDが未設定
  if (!GA4_MEASUREMENT_ID) {
    console.warn('[Analytics] VITE_GA4_MEASUREMENT_ID not configured');
    return;
  }

  try {
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      gaOptions: {
        anonymize_ip: true, // IP匿名化
        cookie_flags: 'SameSite=None;Secure', // Cookie設定
      },
      gtagOptions: {
        send_page_view: false, // 手動でページビュー送信
      },
    });

    config.enabled = true;
    config.initialized = true;

    // 最初のイベント（useAuthFixed の login など）が飛ぶ前に押さえる
    applySanitizedUrl();

    console.log('[Analytics] GA4 initialized successfully');
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
  }
}

/**
 * フルトラッキング有効化（同意取得後に呼び出し）
 * - user_id, patient_id の送信を許可
 */
export function enableFullTracking(): void {
  hasUserConsent = true;
  console.log('[Analytics] Full tracking enabled (user consent granted)');
}

/**
 * フルトラッキングが有効かチェック（同意済みか）
 */
export function isFullTrackingEnabled(): boolean {
  return hasUserConsent;
}

/**
 * 分析を有効化（同意取得後）
 */
export function enableAnalytics(): void {
  if (config.initialized && !config.enabled) {
    config.enabled = true;
    console.log('[Analytics] Analytics enabled');
  }
}

/**
 * 分析を無効化（オプトアウト）
 */
export function disableAnalytics(): void {
  config.enabled = false;
  console.log('[Analytics] Analytics disabled');
}

/**
 * 分析が有効かチェック
 */
export function isAnalyticsEnabled(): boolean {
  return config.enabled && config.initialized;
}

/**
 * 現在選択中の患者IDを設定
 * User Propertyとしても設定（セッション全体に適用、同意後のみ）
 * @param patientId - 患者ID（nullでクリア）
 */
export function setCurrentPatientId(patientId: number | null): void {
  currentPatientId = patientId;

  // User Propertyは同意後のみ設定
  if (isAnalyticsEnabled() && isFullTrackingEnabled() && patientId !== null) {
    try {
      ReactGA.gtag('set', 'user_properties', {
        patient_id: patientId.toString(),
      });
      console.log('[Analytics] Patient ID set:', patientId);
    } catch (error) {
      console.error('[Analytics] Failed to set patient ID:', error);
    }
  } else if (patientId === null) {
    console.log('[Analytics] Patient ID cleared');
  }
}

/**
 * 現在選択中の患者IDを取得
 */
export function getCurrentPatientId(): number | null {
  return currentPatientId;
}

/**
 * ユーザープロパティ設定（同意後のみ実行）
 * @param lineUserId - LINE ユーザーID（ハッシュ化して送信）
 * @param properties - カスタムプロパティ
 */
export async function setUserProperties(
  lineUserId?: string,
  properties?: {
    userRole?: 'self' | 'family'; // 本人 or 家族
    liffContext?: 'app' | 'browser'; // LIFF実行環境
    relationshipType?: string; // 患者との関係（ハッシュ化済み）
  }
): Promise<void> {
  // 同意後のみユーザープロパティを設定
  if (!isAnalyticsEnabled() || !isFullTrackingEnabled()) return;

  try {
    const hashedUserId = lineUserId ? await hashString(lineUserId) : undefined;

    ReactGA.set({
      user_id: hashedUserId, // ハッシュ化されたユーザーID
      ...properties,
    });

    console.log('[Analytics] User properties set');
  } catch (error) {
    console.error('[Analytics] Failed to set user properties:', error);
  }
}

/**
 * ページビュー送信
 * - 同意前: ページビューのみ送信（patient_id なし）
 * - 同意後: patient_id を付与
 *
 * **URL 系 3 フィールドは同じ許可リストを通す。** `page` に渡した値は `page_path` にしかならず、
 * `page_location` と `page_referrer` は gtag が `document.location.href` / `document.referrer` から
 * 自動で埋めるため、明示的に上書きしないと遷移用のパラメータが素通りする
 * （`docs/integrations/ga4.md`「クエリ文字列の扱い」）。後者 2 つは `applySanitizedUrl()` が
 * `gtag('set')` で設定し、**このページビューに限らず以降のイベント全体**に効かせる。
 * 呼び出し側は `path` を `sanitizePagePath()` で洗ってから渡す。
 */
export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled()) return;

  try {
    // ルート変更後の URL に更新する（window.location.href は遷移後の値になっている）
    applySanitizedUrl();

    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
      // 同意後のみ patient_id を付与
      ...(isFullTrackingEnabled() && currentPatientId !== null && {
        patient_id: currentPatientId.toString(),
      }),
    });

    console.log(`[Analytics] Page view: ${path}`, isFullTrackingEnabled() && currentPatientId ? `(patient_id: ${currentPatientId})` : '');
  } catch (error) {
    console.error('[Analytics] Failed to track page view:', error);
  }
}

/**
 * カスタムイベント送信
 * - 同意前: イベント送信（patient_id なし）
 * - 同意後: patient_id を付与
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number
): void {
  if (!isAnalyticsEnabled()) return;

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
      // 同意後のみ patient_id を付与
      ...(isFullTrackingEnabled() && currentPatientId !== null && {
        patient_id: currentPatientId.toString(),
      }),
    });

    console.log(`[Analytics] Event: ${category} - ${action}`, isFullTrackingEnabled() && currentPatientId ? `(patient_id: ${currentPatientId})` : '');
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * GA4推奨イベント送信
 * - 同意前: イベント送信（patient_id なし）
 * - 同意後: patient_id を付与
 */
export function trackGA4Event(
  eventName: string,
  eventParams?: Record<string, unknown>
): void {
  if (!isAnalyticsEnabled()) return;

  try {
    // 同意後のみ patient_id を付与
    const paramsWithPatient = {
      ...(isFullTrackingEnabled() && currentPatientId !== null && {
        patient_id: currentPatientId.toString(),
      }),
      ...eventParams,
    };

    ReactGA.event(eventName, paramsWithPatient);

    console.log(`[Analytics] GA4 Event: ${eventName}`, paramsWithPatient);
  } catch (error) {
    console.error('[Analytics] Failed to track GA4 event:', error);
  }
}

// ===== プリセットイベント =====

/**
 * 認証イベント
 */
export const AuthEvents = {
  loginSuccess: (method: 'line' = 'line') => {
    trackGA4Event('login', { method });
  },
  loginFailed: (reason?: string) => {
    trackGA4Event('login_failed', { reason });
  },
  logout: () => {
    trackGA4Event('logout');
  },
};

/**
 * ユーザーアクションイベント
 */
export const UserEvents = {
  consentGiven: (consentVersion: string) => {
    trackGA4Event('consent_given', { consent_version: consentVersion });
  },
  consentRevoked: () => {
    trackGA4Event('consent_revoked');
  },
  profileCompleted: () => {
    trackGA4Event('profile_completed');
  },
  linkTokenUsed: () => {
    trackGA4Event('link_token_used');
  },
};

/**
 * コンテンツ閲覧イベント
 */
export const ContentEvents = {
  viewContactLog: (date?: string) => {
    trackGA4Event('view_item', {
      item_category: 'contact_log',
      item_name: date || 'latest',
    });
  },
  viewHealthGraph: (period?: string) => {
    trackGA4Event('view_item', {
      item_category: 'health_graph',
      item_name: period || 'default',
    });
  },
  viewImage: (imageType?: string) => {
    trackGA4Event('view_item', {
      item_category: 'image',
      item_name: imageType || 'photo',
    });
  },
};

/**
 * エラーイベント
 */
export const ErrorEvents = {
  apiError: (endpoint: string, statusCode?: number) => {
    trackGA4Event('api_error', {
      endpoint,
      status_code: statusCode,
    });
  },
  liffError: (errorType: string, message?: string) => {
    trackGA4Event('liff_error', {
      error_type: errorType,
      error_message: message?.substring(0, 100), // 最大100文字
    });
  },
  generalError: (errorContext: string, message?: string) => {
    trackGA4Event('error', {
      error_context: errorContext,
      error_message: message?.substring(0, 100),
    });
  },
};

/**
 * インタラクションイベント
 */
export const InteractionEvents = {
  clickNavigation: (destination: string) => {
    trackEvent('Navigation', 'Click', destination);
  },
  clickButton: (buttonName: string) => {
    trackEvent('Button', 'Click', buttonName);
  },
  openModal: (modalName: string) => {
    trackEvent('Modal', 'Open', modalName);
  },
  closeModal: (modalName: string) => {
    trackEvent('Modal', 'Close', modalName);
  },
};
